import React, { useEffect, useState } from 'react';
import {
  Container,
  VStack,
  HStack,
  Box,
  Text,
  Heading,
  Grid,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Button,
  Spinner,
} from '@chakra-ui/react';
import { keyframes } from '@emotion/react';
import { useNavigate } from 'react-router-dom';
import { Zap, Coins, CreditCard, Users, Gift, Search, Play, Trophy, Star, Package, Mic } from 'lucide-react';
import { getVoiceAllowance, formatResetDay, VoiceAccess } from '../services/voiceAccessService';

// Note: Tutorial is now handled at App.tsx level to persist across page navigation
import { POKER_COLORS } from '../theme/designSystem';
import PokerCard from '../components/ui/PokerCard';
import PokerButton from '../components/ui/PokerButton';
import PokerBadge from '../components/ui/PokerBadge';
import LeaderboardPanel from '../components/LeaderboardPanel';
import MonthlyChallengeCard from '../components/MonthlyChallengeCard';
import ActivityFeed from '../components/ActivityFeed';
import '../components/ActivityFeed.css';
import UserSearch from '../components/UserSearch';
import WheelSpin, { WheelSpinButton } from '../components/WheelSpin';
import SocialLinks from '../components/ui/SocialLinks';
import PackDrop from '../components/PackDrop';
import HomeGamePromoModal from '../components/HomeGamePromoModal';
import { auth, db } from '../services/firebase';
import { doc, getDoc, updateDoc, deleteDoc, collection, query, where, getDocs } from 'firebase/firestore';
import walletService from '../services/walletService';
import userService from '../services/userService';
import statsService, { PlatformStats } from '../services/statsService';
import { subscribeToFriendsAtTables, FriendAtTable } from '../services/socialService';
import { subscribeToActiveUsersCount } from '../services/presenceService';
import { findQuickMatchTable } from '../services/quickMatchService';
import { gameApiService } from '../services/gameApiService';

// Animations
const float = keyframes`
  0%, 100% { transform: translateY(0px) rotate(0deg); }
  50% { transform: translateY(-20px) rotate(5deg); }
`;

const pulse = keyframes`
  0%, 100% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.05); opacity: 0.8; }
`;

const shimmer = keyframes`
  0% { background-position: -200% center; }
  100% { background-position: 200% center; }
`;

const coinDrop = keyframes`
  0% { transform: translateY(-100px) rotate(0deg); opacity: 0; }
  10% { opacity: 1; }
  100% { transform: translateY(100vh) rotate(720deg); opacity: 0; }
`;

// Pulsing animation for active users dot
const activePulse = keyframes`
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.6; transform: scale(0.9); }
`;

// Floating Coin Component
const FloatingCoin: React.FC<{ delay: number; left: string; size: number }> = ({ delay, left, size }) => (
  <Box
    position="absolute"
    left={left}
    top="-50px"
    fontSize={`${size}px`}
    animation={`${coinDrop} ${8 + Math.random() * 4}s linear infinite`}
    sx={{ animationDelay: `${delay}s` }}
    opacity={0}
    pointerEvents="none"
    zIndex={0}
  >
    

  </Box>
);

// ============================================================================
// FLOATING ACTIVE PLAYERS WIDGET
// Enterprise-grade widget showing online players count
// Clickable to open UserSearch modal on "Online" tab
// ============================================================================
interface FloatingActivePlayersWidgetProps {
  count: number;
  onClick: () => void;
}

const FloatingActivePlayersWidget: React.FC<FloatingActivePlayersWidgetProps> = ({ count, onClick }) => {
  return (
    <Box
      position="fixed"
      bottom={{ base: '100px', md: '30px' }}
      right={{ base: '16px', md: '30px' }}
      zIndex={999}
      // iOS safe area support
      sx={{
        '@supports (padding-bottom: env(safe-area-inset-bottom))': {
          bottom: { 
            base: 'calc(100px + env(safe-area-inset-bottom))', 
            md: 'calc(30px + env(safe-area-inset-bottom))' 
          },
        },
      }}
    >
      <Box
        as="button"
        onClick={onClick}
        display="flex"
        flexDirection="row"
        alignItems="center"
        gap={2}
        px={4}
        py={3}
        bg="rgba(13, 27, 42, 0.95)"
        border="2px solid"
        borderColor="rgba(34, 197, 94, 0.6)"
        borderRadius="full"
        boxShadow="0 4px 20px rgba(34, 197, 94, 0.3), 0 0 40px rgba(34, 197, 94, 0.1)"
        cursor="pointer"
        transition="all 0.3s ease"
        _hover={{
          transform: 'scale(1.05)',
          borderColor: 'rgba(34, 197, 94, 0.9)',
          boxShadow: '0 6px 30px rgba(34, 197, 94, 0.4), 0 0 60px rgba(34, 197, 94, 0.2)',
          bg: 'rgba(13, 27, 42, 1)',
        }}
        _active={{
          transform: 'scale(0.98)',
        }}
        aria-label={`${count} players online. Click to view online players.`}
      >
        {/* Pulsing green dot */}
        <Box
          w="12px"
          h="12px"
          borderRadius="full"
          bg="#22c55e"
          boxShadow="0 0 10px rgba(34, 197, 94, 0.8), 0 0 20px rgba(34, 197, 94, 0.4)"
          animation={`${activePulse} 2s ease-in-out infinite`}
          flexShrink={0}
        />
        
        {/* Count and label */}
        <HStack spacing={1}>
          <Text
            fontSize="md"
            fontWeight="bold"
            color="#22c55e"
            lineHeight={1}
          >
            {count}
          </Text>
          <Text
            fontSize="sm"
            fontWeight="medium"
            color="rgba(34, 197, 94, 0.8)"
            lineHeight={1}
            display={{ base: 'none', sm: 'block' }}
          >
            Online
          </Text>
        </HStack>
        
        {/* Users icon */}
        <Box display={{ base: 'none', md: 'block' }}>
          <Users size={16} color="#22c55e" />
        </Box>
      </Box>
    </Box>
  );
};

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const [voiceAllowance, setVoiceAllowance] = useState<VoiceAccess | null>(null);
  useEffect(() => {
    getVoiceAllowance().then(setVoiceAllowance).catch(() => setVoiceAllowance(null));
  }, []);
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isSearchOpen, onOpen: onSearchOpen, onClose: onSearchClose } = useDisclosure();
  const { isOpen: isAdminCreditOpen, onOpen: onAdminCreditOpen, onClose: onAdminCreditClose } = useDisclosure();
  
  // Daily bonus state
  const [bonusAmount, setBonusAmount] = useState<number>(0);
  // 👥 GROUPS shelf - popular public groups the user has not joined yet
  const [suggestedGroups, setSuggestedGroups] = useState<any[]>([]);
  const [joiningGroupId, setJoiningGroupId] = useState<string | null>(null);
  const [joinedGroupIds, setJoinedGroupIds] = useState<string[]>([]);
  useEffect(() => {
    let cancelled = false;
    gameApiService.getSuggestedGroups()
      .then(r => { if (!cancelled && r.success) setSuggestedGroups((r.groups || []).slice(0, 3)); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);
  const handleJoinSuggestedGroup = async (gid: string) => {
    setJoiningGroupId(gid);
    const r = await gameApiService.joinGroup({ groupId: gid });
    setJoiningGroupId(null);
    if (r.success) setJoinedGroupIds(p => [...p, gid]);
  };
  const [hasCheckedBonus, setHasCheckedBonus] = useState(false);
  // 🎡 Daily wheel spin modal
  const [wheelOpen, setWheelOpen] = useState(false);
  // 📦 Pack Drop (fortnightly). Deep link ?packDrop=1 opens it from the push.
  const [packOpen, setPackOpen] = useState(false);
  const [currentUid, setCurrentUid] = useState<string | undefined>(undefined);
  const [currentUsername, setCurrentUsername] = useState<string>('Player');
  
  // Admin credit notification state
  const [adminCredit, setAdminCredit] = useState<{
    amount: number;
    currency: string;
    message: string;
  } | null>(null);

  //  Platform stats state

  const [platformStats, setPlatformStats] = useState<PlatformStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(true);

  //  Friends at tables state

  const [friendsAtTables, setFriendsAtTables] = useState<FriendAtTable[]>([]);

  //  Active users count for floating widget (real-time)

  const [activeUsersCount, setActiveUsersCount] = useState<number>(0);

  //  Default tab for UserSearch modal (0=All, 1=Online, 2=Friends)

  const [searchDefaultTab, setSearchDefaultTab] = useState<number>(0);

  //  Quick Match state

  const [quickMatchLoading, setQuickMatchLoading] = useState(false);

  //  Subscribe to active users count (real-time updates)

  useEffect(() => {
    const unsubscribe = subscribeToActiveUsersCount((count) => {
      setActiveUsersCount(count);
    });
    return () => unsubscribe();
  }, []);

  //  Handler for floating widget click

  const handleActivePlayersClick = () => {
    setSearchDefaultTab(1); // Open to "Online" tab
    onSearchOpen();
  };

  //  Handler for regular search open (default tab)

  const handleSearchOpen = () => {
    setSearchDefaultTab(0); // Default to "All" tab
    onSearchOpen();
  };

  //  Handler for search close (reset default tab)

  const handleSearchClose = () => {
    onSearchClose();
    setSearchDefaultTab(0); // Reset to default
  };

  //  Quick Match handler

  const handleQuickMatch = async () => {
    if (!currentUid) {
      toast({
        title: 'Please log in',
        description: 'You need to be logged in to use Quick Match',
        status: 'warning',
        duration: 3000,
      });
      return;
    }

    setQuickMatchLoading(true);
    try {
      const result = await findQuickMatchTable(currentUid, {
        preferredCurrency: 'playcoins',
      });

      if (result.success && result.table) {
        toast({
          title: 'Table Found!',

          description: `Joining ${result.table.name}...`,
          status: 'success',
          duration: 2000,
        });
        navigate(`/game/${result.table.id}`);
      } else {
        toast({
          title: 'No tables available',
          description: result.message || 'Try browsing tables manually',
          status: 'info',
          duration: 3000,
        });
        navigate('/tables');
      }
    } catch (error) {
      console.error('Quick match error:', error);
      toast({
        title: 'Quick Match Failed',
        description: 'Please try again or browse tables',
        status: 'error',
        duration: 3000,
      });
    } finally {
      setQuickMatchLoading(false);
    }
  };

  //  Fetch real platform stats

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setStatsLoading(true);
        const stats = await statsService.getPlatformStats();
        setPlatformStats(stats);
      } catch (error) {
        console.error('Error fetching platform stats:', error);
      } finally {
        setStatsLoading(false);
      }
    };

    fetchStats();

    // Refresh stats every 30 seconds
    const intervalId = setInterval(fetchStats, 30000);
    return () => clearInterval(intervalId);
  }, []);

  // Get current user info
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (user) {
        setCurrentUid(user.uid);
        try {
          const username = await userService.getUsername(user.uid);
          setCurrentUsername(username || 'Player');
        } catch (error) {
          console.error('Error getting username:', error);
        }
      }
    });
    return () => unsubscribe();
  }, []);

  //  Subscribe to friends at tables

  useEffect(() => {
    if (!currentUid) {
      console.log('👥 [HomePage] No currentUid yet, skipping friends subscription');
      return;
    }
    console.log('👥 [HomePage] Subscribing to friends at tables for:', currentUid);
    const unsubscribe = subscribeToFriendsAtTables(currentUid, (friends) => {
      console.log('👥 [HomePage] Received friends update:', friends.length, friends);
      setFriendsAtTables(friends);
    });
    return () => unsubscribe();
  }, [currentUid]);

  // Check for admin credits on login
  useEffect(() => {
    const checkAdminCredits = async () => {
      const user = auth.currentUser;
      if (!user) return;

      try {
        // Check for pending admin credits
        const creditsQuery = query(
          collection(db, 'adminCredits'),
          where('userId', '==', user.uid),
          where('claimed', '==', false)
        );
        const creditsSnap = await getDocs(creditsQuery);
        
        if (!creditsSnap.empty) {
          const creditDoc = creditsSnap.docs[0];
          const creditData = creditDoc.data();
          
          setAdminCredit({
            amount: creditData.amount,
            currency: creditData.currency,
            message: creditData.message || 'You received a bonus!',
          });
          
          // Mark as claimed and delete
          await deleteDoc(doc(db, 'adminCredits', creditDoc.id));
          onAdminCreditOpen();
        }
      } catch (error) {
        console.error('Error checking admin credits:', error);
      }
    };

    if (currentUid) {
      checkAdminCredits();
    }
  }, [currentUid, onAdminCreditOpen]);

  // Opened straight from the Pack Drop push (?packDrop=1).
  useEffect(() => {
    try {
      if (new URLSearchParams(window.location.search).get('packDrop') === '1') {
        setPackOpen(true);
        window.history.replaceState({}, '', window.location.pathname);
      }
    } catch { /* noop */ }
  }, []);

  // Check and claim daily bonus on page load
  useEffect(() => {
    const checkDailyBonus = async () => {
      const user = auth.currentUser;
      if (!user || hasCheckedBonus) return;

      // hasCheckedBonus is component state, so it resets every time Home
      // remounts - navigating away and back fired a fresh claim each time.
      // Pin the guard to the DAY instead so one visit per day attempts it.
      // (The server is idempotent per user per day regardless; this just
      // stops the pointless round trips.)
      const bonusKey = `pss:dailyBonus:${user.uid}:${new Date().toISOString().split('T')[0]}`;
      try {
        if (sessionStorage.getItem(bonusKey)) { setHasCheckedBonus(true); return; }
        sessionStorage.setItem(bonusKey, '1');
      } catch { /* sessionStorage unavailable - server still guards it */ }

      setHasCheckedBonus(true);
      
      try {
        console.log('🎁 Checking daily bonus for user:', user.uid);
        const amount = await walletService.claimDailyBonus(user.uid);
        
        if (amount > 0) {
          setBonusAmount(amount);
          // Never pop the bonus modal over a running tour - it lands inside
          // the spotlight and fights the tour dialog for the screen. The
          // claim itself went through; the modal just stays quiet.
          if (document.body.dataset.pssTourActive !== '1') {
            onOpen();
          }

          toast({
            title: 'Daily Bonus Claimed!',

            description: `You received ${amount.toLocaleString()} PlayCoins!`,
            status: 'success',
            duration: 5000,
            isClosable: true,
            position: 'top',
          });
          
          console.log(`🎁 Daily bonus claimed: ${amount} PlayCoins`);
        } else {
          console.log('ℹ️ Daily bonus already claimed today');
        }
      } catch (error) {
        console.error('Error checking daily bonus:', error);
      }
    };

    const timer = setTimeout(checkDailyBonus, 500);
    return () => clearTimeout(timer);
  }, [hasCheckedBonus, onOpen, toast]);

  //  Format currency for display

  const formatCurrency = (amount: number): string => {
    if (amount >= 1000000) {
      return `${(amount / 1000000).toFixed(2)}M`;
    } else if (amount >= 1000) {
      return `${(amount / 1000).toFixed(1)}K`;
    }
    return `$${amount.toLocaleString()}`;
  };

  //  Format number with commas

  const formatNumber = (num: number): string => {
    return num.toLocaleString();
  };

  return (
    <Box 
      minH="100vh" 
      position="relative" 
      overflow="hidden"
      bg="linear-gradient(180deg, #1a0a0a 0%, #2d1810 50%, #1a0a0a 100%)"
    >
      {/* Animated Background Pattern */}
      <Box
        position="absolute"
        top={0}
        left={0}
        right={0}
        bottom={0}
        opacity={0.08}
        backgroundImage="repeating-linear-gradient(45deg, transparent, transparent 35px, rgba(255,215,0,0.03) 35px, rgba(255,215,0,0.03) 70px)"
        pointerEvents="none"
      />

      {/* Floating Coins - Hidden on mobile for performance */}
      <Box display={{ base: 'none', md: 'block' }}>
        {[...Array(8)].map((_, i) => (
          <FloatingCoin key={i} delay={i * 1.5} left={`${10 + i * 12}%`} size={24 + Math.random() * 16} />
        ))}
      </Box>

      {/* FIXED: Centered container with symmetric padding */}
      <Container 
        maxW="6xl" 
        py={{ base: 4, md: 8 }} 
        px={{ base: 3, md: 6 }}
        pb={{ base: 24, md: 8 }} 
        position="relative" 
        zIndex={1}
      >
        <Grid templateColumns={{ base: '1fr', lg: '1fr 380px' }} gap={{ base: 4, md: 8 }}>
          {/* Main Content */}
          <VStack spacing={{ base: 4, md: 8 }} align="stretch">
            {/* Hero Section - FIXED: Smaller on mobile */}
            <Box 
              textAlign="center" 
              py={{ base: 5, md: 8 }}
              px={{ base: 3, md: 4 }}
              bg="linear-gradient(135deg, rgba(139,0,0,0.4) 0%, rgba(0,0,0,0.6) 100%)"
              borderRadius="2xl"
              border="2px solid"
              borderColor="yellow.600"
              position="relative"
              overflow="hidden"
              data-tutorial="hero-section"
            >
              {/* Decorative Elements - Smaller on mobile */}
              <Box position="absolute" top={{ base: 2, md: 4 }} left={{ base: 2, md: 4 }} fontSize={{ base: 'xl', md: '3xl' }} opacity={0.3}></Box>

              <Box position="absolute" top={{ base: 2, md: 4 }} right={{ base: 2, md: 4 }} fontSize={{ base: 'xl', md: '3xl' }} opacity={0.3}></Box>

              <Box position="absolute" bottom={{ base: 2, md: 4 }} left={{ base: 2, md: 4 }} fontSize={{ base: 'xl', md: '3xl' }} opacity={0.3}></Box>

              <Box position="absolute" bottom={{ base: 2, md: 4 }} right={{ base: 2, md: 4 }} fontSize={{ base: 'xl', md: '3xl' }} opacity={0.3}></Box>


              <VStack spacing={{ base: 2, md: 4 }}>
                <Text 
                  color="yellow.400" 
                  fontSize={{ base: 'xs', md: 'sm' }}
                  fontWeight="bold"
                  textTransform="uppercase"
                  letterSpacing="wider"
                >
                   Welcome to

                </Text>
                
                <Heading 
                  color="white" 
                  fontSize={{ base: '2xl', sm: '3xl', md: '5xl' }}
                  fontWeight="black"
                  textShadow="0 0 40px rgba(255,215,0,0.5)"
                >
                  PokerSweepStars
                </Heading>

                <Box
                  bg="linear-gradient(90deg, transparent, rgba(255,215,0,0.1), transparent)"
                  bgSize="200% 100%"
                  animation={`${shimmer} 3s linear infinite`}
                  px={{ base: 2, md: 6 }}
                  py={{ base: 2, md: 3 }}
                  borderRadius="xl"
                  border="1px solid"
                  borderColor="yellow.600"
                  maxW="100%"
                >
                  <Text 
                    color="gray.300" 
                    fontSize={{ base: 'xs', sm: 'sm', md: 'lg' }} 
                    m={0} 
                    textAlign="center"
                    whiteSpace="normal"
                    wordBreak="break-word"
                    lineHeight="1.6"
                  >
                    Play for free with PlayCoins or win{' '}
                    <Text as="span" color="yellow.400" fontWeight="black" fontSize={{ base: 'sm', sm: 'md', md: '2xl' }}>
                      rewards
                    </Text>
                    {' '}where eligible*
                  </Text>
                  <Text color="gray.500" fontSize="2xs" textAlign="center" mt={1}>
                    *PSS Coins available in eligible US states only
                  </Text>
                </Box>

                {/* WHAT'S NEW - cinematic tour entry. Big gold draw with a
                    glow pulse until watched once (localStorage-tracked). */}
                <Box
                  as="button"
                  onClick={() => navigate('/whats-new')}
                  mt={3}
                  px={{ base: 8, md: 12 }}
                  py={{ base: 3, md: 4 }}
                  borderRadius="full"
                  border="none"
                  bg="linear-gradient(135deg, #f5d976 0%, #d4af37 55%, #a97f1f 100%)"
                  color="#181205"
                  fontWeight="900"
                  fontSize={{ base: 'sm', md: 'md' }}
                  letterSpacing="0.2em"
                  cursor="pointer"
                  position="relative"
                  boxShadow="0 8px 34px rgba(212, 175, 55, 0.5)"
                  _hover={{ transform: 'scale(1.05)', boxShadow: '0 8px 46px rgba(212, 175, 55, 0.8)' }}
                  transition="all 0.2s ease"
                  sx={(() => {
                    let seen = false;
                    try { seen = !!localStorage.getItem('pss:whatsNewSeen:v1'); } catch { /* noop */ }
                    return seen ? {} : {
                      animation: 'wnHomePulse 1.7s ease infinite',
                      '@keyframes wnHomePulse': {
                        '0%, 100%': { transform: 'scale(1)', boxShadow: '0 8px 26px rgba(212,175,55,0.45)' },
                        '50%': { transform: 'scale(1.045)', boxShadow: '0 8px 50px rgba(212,175,55,0.85)' },
                      },
                    };
                  })()}
                >
                  SEE WHAT'S NEW
                  {(() => {
                    try { if (localStorage.getItem('pss:whatsNewSeen:v1')) return null; } catch { /* noop */ }
                    return (
                      <Box as="span" position="absolute" top="-8px" right="-8px"
                        bg="red.500" color="white" borderRadius="full"
                        fontSize="10px" px={2} py={0.5} fontWeight="black"
                        boxShadow="0 0 12px rgba(239,68,68,0.7)">
                        NEW
                      </Box>
                    );
                  })()}
                </Box>

                {/* FIXED: Stack buttons vertically on mobile, horizontally on tablet+ */}
                <VStack 
                  spacing={{ base: 3, sm: 0 }} 
                  pt={{ base: 2, md: 4 }}
                  w="100%"
                  align="center"
                >
                  {/* Mobile: Stack vertically. Tablet+: HStack */}
                  <HStack
                    spacing={{ base: 2, md: 4 }}
                    display={{ base: 'none', sm: 'flex' }}
                    flexWrap="wrap"
                    justify="center"
                  >
                    {/*  Quick Match Button */}

                    <PokerButton
                      variant="primary"
                      size={{ base: 'md', md: 'lg' }}
                      onClick={handleQuickMatch}
                      isLoading={quickMatchLoading}
                      loadingText="Finding..."
                      bg="linear-gradient(135deg, #22c55e 0%, #15803d 100%)"
                      border="2px solid"
                      borderColor="green.400"
                      px={{ base: 6, md: 8 }}
                      _hover={{ transform: 'scale(1.05)', boxShadow: '0 0 30px rgba(34,197,94,0.5)' }}
                      data-tutorial="quick-match-button"
                    >
                      <Zap size={20} style={{ marginRight: '8px' }} />
                      Quick Match
                    </PokerButton>

                    <PokerButton
                      variant="primary"
                      size={{ base: 'md', md: 'lg' }}
                      onClick={() => navigate('/tables')}
                      bg="linear-gradient(135deg, #dc2626 0%, #991b1b 100%)"
                      border="2px solid"
                      borderColor="yellow.500"
                      px={{ base: 6, md: 8 }}
                      _hover={{ transform: 'scale(1.05)', boxShadow: '0 0 30px rgba(255,215,0,0.5)' }}
                      animation={`${pulse} 2s ease-in-out infinite`}
                      data-tutorial="play-button"
                    >
                      <Play size={20} style={{ marginRight: '8px' }} />
                      Play Now
                    </PokerButton>
                    
                    <PokerButton
                      variant="secondary"
                      size={{ base: 'md', md: 'lg' }}
                      onClick={() => navigate('/buy-coins')}
                      borderColor="yellow.500"
                      _hover={{ bg: 'rgba(255,215,0,0.1)' }}
                      data-tutorial="get-coins-button"
                    >
                      <Coins size={20} style={{ marginRight: '8px' }} />
                      Get Coins
                    </PokerButton>
                  </HStack>

                  {/* Mobile only: Stacked buttons */}
                  <VStack 
                    spacing={3} 
                    w="100%" 
                    maxW="280px"
                    display={{ base: 'flex', sm: 'none' }}
                  >
                    <PokerButton
                      variant="primary"
                      size="md"
                      onClick={handleQuickMatch}
                      isLoading={quickMatchLoading}
                      loadingText="Finding..."
                      bg="linear-gradient(135deg, #22c55e 0%, #15803d 100%)"
                      border="2px solid"
                      borderColor="green.400"
                      w="100%"
                      _hover={{ transform: 'scale(1.02)', boxShadow: '0 0 20px rgba(34,197,94,0.5)' }}
                      data-tutorial="quick-match-button"
                    >
                      <Zap size={18} style={{ marginRight: '6px' }} />
                      Quick Match
                    </PokerButton>

                    <PokerButton
                      variant="primary"
                      size="md"
                      onClick={() => navigate('/tables')}
                      bg="linear-gradient(135deg, #dc2626 0%, #991b1b 100%)"
                      border="2px solid"
                      borderColor="yellow.500"
                      w="100%"
                      _hover={{ transform: 'scale(1.02)', boxShadow: '0 0 20px rgba(255,215,0,0.5)' }}
                      animation={`${pulse} 2s ease-in-out infinite`}
                      data-tutorial="play-button"
                    >
                      <Play size={18} style={{ marginRight: '6px' }} />
                      Play Now
                    </PokerButton>
                    
                    <PokerButton
                      variant="secondary"
                      size="md"
                      onClick={() => navigate('/buy-coins')}
                      borderColor="yellow.500"
                      w="100%"
                      _hover={{ bg: 'rgba(255,215,0,0.1)' }}
                      data-tutorial="get-coins-button"
                    >
                      <Coins size={18} style={{ marginRight: '6px' }} />
                      Get Coins
                    </PokerButton>
                  </VStack>
                </VStack>
              </VStack>
            </Box>

            {/*  UPDATED: Quick Stats with REAL DATA */}

            <Grid templateColumns="repeat(3, 1fr)" gap={{ base: 2, md: 4 }} data-tutorial="stats-section">
              <PokerCard 
                textAlign="center" 
                py={{ base: 3, md: 4 }}
                px={{ base: 2, md: 4 }}
                bg="linear-gradient(135deg, rgba(0,217,217,0.1) 0%, rgba(0,0,0,0.3) 100%)"
                border="1px solid"
                borderColor="cyan.700"
              >
                <Users color="#00d9d9" size={20} style={{ margin: '0 auto 8px' }} />
                {statsLoading ? (
                  <Spinner size="sm" color="cyan.400" />
                ) : (
                  <Text color="cyan.400" fontSize={{ base: 'lg', md: '2xl' }} fontWeight="bold">
                    {/*  REAL DATA: Total registered users */}

                    {formatNumber(platformStats?.totalUsers || 0)}
                  </Text>
                )}
                <Text color="gray.500" fontSize={{ base: '2xs', md: 'xs' }}>Total Players</Text>
              </PokerCard>

              <PokerCard 
                textAlign="center" 
                py={{ base: 3, md: 4 }}
                px={{ base: 2, md: 4 }}
                bg="linear-gradient(135deg, rgba(255,215,0,0.1) 0%, rgba(0,0,0,0.3) 100%)"
                border="1px solid"
                borderColor="yellow.700"
              >
                <Trophy color="#fbbf24" size={20} style={{ margin: '0 auto 8px' }} />
                {statsLoading ? (
                  <Spinner size="sm" color="yellow.400" />
                ) : (
                  <Text color="yellow.400" fontSize={{ base: 'lg', md: '2xl' }} fontWeight="bold">
                    {/*  REAL DATA: Total PSS Coins in circulation */}

                    {formatCurrency(platformStats?.totalPKCashInCirculation || 0)}
                  </Text>
                )}
                <Text color="gray.500" fontSize={{ base: '2xs', md: 'xs' }}>PSS Coins Pool</Text>
              </PokerCard>

              <PokerCard 
                textAlign="center" 
                py={{ base: 3, md: 4 }}
                px={{ base: 2, md: 4 }}
                bg="linear-gradient(135deg, rgba(34,197,94,0.1) 0%, rgba(0,0,0,0.3) 100%)"
                border="1px solid"
                borderColor="green.700"
              >
                <Zap color="#22c55e" size={20} style={{ margin: '0 auto 8px' }} />
                {statsLoading ? (
                  <Spinner size="sm" color="green.400" />
                ) : (
                  <Text color="green.400" fontSize={{ base: 'lg', md: '2xl' }} fontWeight="bold">
                    {/*  REAL DATA: Active tables count */}

                    {formatNumber(platformStats?.activeTables || 0)}
                  </Text>
                )}
                <Text color="gray.500" fontSize={{ base: '2xs', md: 'xs' }}>Active Tables</Text>
              </PokerCard>
            </Grid>

            {/* Game Mode Cards */}
            <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={{ base: 3, md: 4 }} data-tutorial="game-modes">
              <PokerCard
                p={{ base: 4, md: 6 }}
                bg="linear-gradient(135deg, rgba(0,217,217,0.15) 0%, rgba(13,56,71,0.9) 100%)"
                border="2px solid"
                borderColor="cyan.600"
                cursor="pointer"
                onClick={() => navigate('/tables')}
                _hover={{ transform: 'translateY(-4px)', boxShadow: '0 10px 40px rgba(0,217,217,0.3)' }}
                transition="all 0.3s"
              >
                <HStack spacing={{ base: 3, md: 4 }}>
                  <Box 
                    bg="cyan.500" 
                    p={{ base: 2, md: 3 }}
                    borderRadius="xl"
                    animation={`${float} 3s ease-in-out infinite`}
                  >
                    <Coins color="white" size={24} />
                  </Box>
                  <VStack align="start" spacing={0}>
                    <Text color="cyan.400" fontWeight="bold" fontSize={{ base: 'md', md: 'lg' }}>PlayCoins Tables</Text>
                    <Text color="gray.400" fontSize={{ base: 'xs', md: 'sm' }}>Free-to-play poker • Daily bonuses</Text>
                  </VStack>
                </HStack>
                <Text color="gray.500" fontSize="xs" mt={{ base: 2, md: 3 }}>
                  Practice your skills risk-free with virtual chips
                </Text>
              </PokerCard>

              <PokerCard
                p={{ base: 4, md: 6 }}
                bg="linear-gradient(135deg, rgba(255,215,0,0.15) 0%, rgba(71,56,13,0.9) 100%)"
                border="2px solid"
                borderColor="yellow.600"
                cursor="pointer"
                onClick={() => navigate('/tables')}
                _hover={{ transform: 'translateY(-4px)', boxShadow: '0 10px 40px rgba(255,215,0,0.3)' }}
                transition="all 0.3s"
              >
                <HStack spacing={{ base: 3, md: 4 }}>
                  <Box 
                    bg="yellow.500" 
                    p={{ base: 2, md: 3 }}
                    borderRadius="xl"
                    animation={`${float} 3s ease-in-out infinite`}
                    sx={{ animationDelay: "0.5s" }}
                  >
                    <CreditCard color="black" size={24} />
                  </Box>
                  <VStack align="start" spacing={0}>
                    <Text color="yellow.400" fontWeight="bold" fontSize={{ base: 'md', md: 'lg' }}>PSS Coins Tables</Text>
                    <Text color="gray.400" fontSize={{ base: 'xs', md: 'sm' }}>Premium Tables • Compete</Text>
                  </VStack>
                </HStack>
                <Text color="gray.500" fontSize="xs" mt={{ base: 2, md: 3 }}>
                  Play at premium tables with PSS Coins
                </Text>
              </PokerCard>
            </Grid>

            {/*  Friends Playing Section */}

            {friendsAtTables.length > 0 ? (
              <Box
                p={{ base: 3, md: 4 }}
                bg="linear-gradient(135deg, rgba(139,92,246,0.15) 0%, rgba(13,27,42,0.9) 100%)"
                borderRadius="xl"
                border="1px solid"
                borderColor="purple.600"
                data-tutorial="friends-section"
              >
                <HStack justify="space-between" mb={3}>
                  <HStack spacing={2}>
                    <Users color="#a78bfa" size={20} />
                    <Text color="purple.400" fontWeight="bold" fontSize={{ base: 'sm', md: 'md' }}>
                      Friends Playing Now
                    </Text>
                  </HStack>
                  <Text
                    color="purple.400"
                    fontSize="xs"
                    cursor="pointer"
                    _hover={{ color: 'purple.300', textDecoration: 'underline' }}
                    onClick={() => navigate('/tables')}
                  >
                    See All →
                  </Text>
                </HStack>
                
                <VStack spacing={2} align="stretch">
                  {friendsAtTables.slice(0, 3).map((friend) => (
                    <HStack
                      key={`${friend.odid}-${friend.tableId}`}
                      p={2}
                      bg="rgba(0,0,0,0.3)"
                      borderRadius="lg"
                      justify="space-between"
                    >
                      <HStack spacing={3}>
                        <Box
                          w="36px"
                          h="36px"
                          borderRadius="full"
                          bg="purple.600"
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          overflow="hidden"
                        >
                          {friend.photoURL ? (
                            <img
                              src={friend.photoURL}
                              alt={friend.username}
                              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            />
                          ) : (
                            <Text color="white" fontWeight="bold" fontSize="sm">
                              {friend.username.charAt(0).toUpperCase()}
                            </Text>
                          )}
                        </Box>
                        <VStack align="start" spacing={0}>
                          <Text color="white" fontWeight="semibold" fontSize="sm">
                            {friend.username}
                          </Text>
                          <HStack spacing={1}>
                            <Text color="gray.500" fontSize="xs">{friend.tableName}</Text>
                            <Text color="gray.600" fontSize="xs">•</Text>
                            <Text color={friend.currency === 'pkcash' ? 'yellow.400' : 'cyan.400'} fontSize="xs">
                              {friend.smallBlind}/{friend.bigBlind}
                            </Text>
                            <Text color="gray.600" fontSize="xs">•</Text>
                            <Text color="gray.500" fontSize="xs">{friend.playerCount}/{friend.maxSeats}</Text>
                          </HStack>
                        </VStack>
                      </HStack>
                      <Button
                        size="sm"
                        colorScheme="purple"
                        variant="solid"
                        fontSize="xs"
                        h="28px"
                        onClick={() => navigate(`/game/${friend.tableId}`)}
                      >
                        Join
                      </Button>
                    </HStack>
                  ))}
                </VStack>
              </Box>
            ) : (
              <Box
                p={{ base: 3, md: 4 }}
                bg="linear-gradient(135deg, rgba(139,92,246,0.1) 0%, rgba(13,27,42,0.5) 100%)"
                borderRadius="xl"
                border="1px dashed"
                borderColor="purple.800"
                data-tutorial="friends-section"
              >
                <HStack spacing={2} justify="center">
                  <Users color="#6b7280" size={18} />
                  <Text color="gray.500" fontSize="sm">
                    No friends playing right now
                  </Text>
                </HStack>
              </Box>
            )}

            {/* Free voice minutes this week */}
            {voiceAllowance && (
              <Box
                p={{ base: 3, md: 4 }}
                bg="rgba(13,27,42,0.9)"
                borderRadius="xl"
                border="1px solid"
                borderColor={voiceAllowance.mode === 'exhausted' ? 'whiteAlpha.200' : 'cyan.800'}
              >
                <HStack justify="space-between" flexWrap="wrap" gap={2}>
                  <HStack spacing={2}>
                    <Mic color={voiceAllowance.mode === 'exhausted' ? '#6b7280' : '#22d3ee'} size={18} />
                    <Text color="white" fontSize="sm" fontWeight="semibold">
                      {voiceAllowance.mode === 'pass'
                        ? 'Unlimited voice and video (pass active)'
                        : voiceAllowance.remainingSeconds > 0
                          ? `${Math.ceil(voiceAllowance.remainingSeconds / 60)} free voice and video minutes left this week`
                          : 'Free voice minutes used up this week'}
                    </Text>
                  </HStack>
                  {voiceAllowance.mode !== 'pass' && (
                    <Text color="gray.500" fontSize="xs">Resets {formatResetDay(voiceAllowance.resetsAtMs)}</Text>
                  )}
                </HStack>
              </Box>
            )}

            {/* 👥 GROUPS - the find-a-group shelf. First-class on Home so
                nobody has to hunt for it: popular public groups with a
                one-tap Join, and the door to the full Groups area. */}
            <Box
              p={{ base: 3, md: 4 }}
              bg="linear-gradient(135deg, rgba(34,211,238,0.12) 0%, rgba(13,27,42,0.9) 100%)"
              borderRadius="xl"
              border="1px solid"
              borderColor="cyan.700"
            >
              <HStack justify="space-between" mb={1}>
                <HStack spacing={2}>
                  <Users color="#22d3ee" size={20} />
                  <Text color="cyan.300" fontWeight="bold" fontSize={{ base: 'sm', md: 'md' }}>
                    Find Your Crew
                  </Text>
                </HStack>
                <Text
                  color="cyan.400"
                  fontSize="xs"
                  cursor="pointer"
                  _hover={{ color: 'cyan.300', textDecoration: 'underline' }}
                  onClick={() => navigate('/groups')}
                >
                  All Groups →
                </Text>
              </HStack>
              <Text color="gray.500" fontSize="xs" mb={3}>
                Join a group to rally games together, schedule poker nights, and
                get pinged when your crew sits down.
              </Text>
              {suggestedGroups.length > 0 && (
                <VStack spacing={2} align="stretch" mb={2}>
                  {suggestedGroups.map((g: any) => {
                    const joined = joinedGroupIds.includes(g.id);
                    return (
                      <HStack key={g.id} p={2} bg="rgba(0,0,0,0.3)" borderRadius="lg" justify="space-between"
                        cursor="pointer" onClick={() => navigate(`/groups/${g.id}`)}>
                        <VStack align="start" spacing={0} minW={0}>
                          <Text color="white" fontWeight="semibold" fontSize="sm" noOfLines={1}>{g.name}</Text>
                          <Text color="gray.500" fontSize="xs" noOfLines={1}>
                            {g.memberCount} member{g.memberCount === 1 ? '' : 's'}{g.description ? ` - ${g.description}` : ''}
                          </Text>
                        </VStack>
                        <Button
                          size="sm" fontSize="xs" h="28px" flexShrink={0}
                          colorScheme={joined ? 'green' : 'cyan'}
                          variant={joined ? 'outline' : 'solid'}
                          isLoading={joiningGroupId === g.id}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (joined) navigate(`/groups/${g.id}`);
                            else handleJoinSuggestedGroup(g.id);
                          }}
                        >
                          {joined ? 'View' : 'Join'}
                        </Button>
                      </HStack>
                    );
                  })}
                </VStack>
              )}
              <Button
                w="100%" size="sm" variant="outline" colorScheme="cyan"
                leftIcon={<Search size={14} />}
                onClick={() => navigate('/groups')}
              >
                Find a group
              </Button>
            </Box>

            {/* Daily Bonus Banner */}
            <Box
              p={{ base: 3, md: 5 }}
              bg="linear-gradient(135deg, rgba(255,215,0,0.2) 0%, rgba(139,0,0,0.3) 100%)"
              borderRadius="xl"
              border="2px solid"
              borderColor="yellow.600"
              position="relative"
              overflow="hidden"
              data-tutorial="daily-bonus"
            >
              <Box
                position="absolute"
                top={0}
                left={0}
                right={0}
                bottom={0}
                bg="linear-gradient(90deg, transparent, rgba(255,215,0,0.05), transparent)"
                bgSize="200% 100%"
                animation={`${shimmer} 4s linear infinite`}
                pointerEvents="none"
              />
              <HStack spacing={{ base: 3, md: 4 }} flexWrap={{ base: 'wrap', sm: 'nowrap' }}>
                <Box
                  bg="yellow.500"
                  p={{ base: 3, md: 4 }}
                  borderRadius="full"
                  animation={`${float} 3s ease-in-out infinite`}
                  flexShrink={0}
                >
                  <Gift color="#1a0a0a" size={24} />
                </Box>
                <VStack align="start" spacing={1}>
                  <Text color="yellow.400" fontWeight="bold" fontSize={{ base: 'md', md: 'xl' }}>
                     Daily Login Bonus

                  </Text>
                  <Text color="gray.300" fontSize={{ base: 'xs', md: 'sm' }}>
                    Login every day to receive <Text as="span" color="yellow.400" fontWeight="bold">1,000 PlayCoins</Text> FREE!
                  </Text>
                  <Text color="gray.400" fontSize={{ base: '2xs', md: 'sm' }}>
                    Plus: Buy PlayCoins → Get PSS Coins bonus instantly!
                  </Text>
                </VStack>
              </HStack>
            </Box>

            {/* 🎡 DAILY WHEEL SPIN - sits with the daily bonus because it's
                the same habit: one free spin every day. The button shows a
                live "available / come back in Xh" state and opens the wheel
                modal; the spin itself is a server callable. */}
            {currentUid && (
              <Box data-tutorial="daily-spin">
                <WheelSpinButton
                  userId={currentUid}
                  onClick={() => setWheelOpen(true)}
                />
              </Box>
            )}

            {/* 📦 PACK DROP - fortnightly, unlocked by playing a hand */}
            {currentUid && (
              <Box
                as="button"
                data-tutorial="pack-drop-card"
                onClick={() => setPackOpen(true)}
                w="100%"
                textAlign="left"
                p={{ base: 3, md: 4 }}
                borderRadius="xl"
                border="2px solid"
                borderColor="rgba(0,217,217,0.45)"
                bg="linear-gradient(135deg, rgba(0,217,217,0.10) 0%, rgba(13,40,56,0.6) 100%)"
                _hover={{ borderColor: 'rgba(0,217,217,0.8)' }}
                transition="border-color .2s"
              >
                <HStack spacing={{ base: 3, md: 4 }}>
                  <Box
                    flexShrink={0}
                    w={{ base: '44px', md: '52px' }}
                    h={{ base: '44px', md: '52px' }}
                    borderRadius="lg"
                    display="grid"
                    placeItems="center"
                    color="#241c00"
                    bg="radial-gradient(circle at 35% 30%, #ffe680, #FFD700 55%, #a8860b)"
                  >
                    <Package size={24} strokeWidth={2.2} />
                  </Box>
                  <VStack align="start" spacing={0}>
                    <Text color="cyan.300" fontWeight="bold" fontSize={{ base: 'md', md: 'lg' }}>
                      Pack Drop
                    </Text>
                    <Text color="gray.400" fontSize={{ base: 'xs', md: 'sm' }}>
                      Free pack twice a week · play a hand to unlock
                    </Text>
                  </VStack>
                </HStack>
              </Box>
            )}

            {/* Info Footer */}
            <Box
              textAlign="center"
              p={{ base: 3, md: 4 }}
              bg="rgba(0,0,0,0.5)"
              borderRadius="lg"
              border="1px solid"
              borderColor="gray.700"
            >
              <Text color="gray.500" fontSize={{ base: '2xs', md: 'sm' }}>
                18+ only. Social gaming platform. Responsible gaming. Play responsibly.
              </Text>
              {/* Official accounts - one source of truth in SocialLinks */}
              <Box mt={3} pt={3} borderTop="1px solid" borderColor="whiteAlpha.100">
                <Text color="gray.600" fontSize="2xs" letterSpacing="0.16em" mb={2}>
                  FOLLOW US
                </Text>
                <SocialLinks />
              </Box>
            </Box>
          </VStack>

          {/* Right Sidebar - Leaderboard & Social */}
          <VStack spacing={6} align="stretch" display={{ base: 'none', lg: 'flex' }}>
            {/* Find Players Button */}
            <Button
              leftIcon={<Search size={18} />}
              bg="linear-gradient(135deg, #00d9d9 0%, #0088aa 100%)"
              color="white"
              fontWeight="bold"
              _hover={{ transform: 'scale(1.02)', boxShadow: '0 0 20px rgba(0,217,217,0.5)' }}
              onClick={handleSearchOpen}
              h="50px"
              borderRadius="xl"
              data-tutorial="find-players"
            >
              Find Players
            </Button>

            {/*  Activity Feed - Live activity from across the platform */}

            <ActivityFeed maxItems={8} compact={false} showHeader={true} showViewAll={true} />

            {/* Leaderboard */}
            <Box data-tutorial="leaderboard">
              <LeaderboardPanel currentUid={currentUid} maxEntries={10} />
            </Box>

            {/*  Monthly Challenge */}

            <MonthlyChallengeCard currentUid={currentUid} maxEntries={5} />
          </VStack>

          {/* Mobile Leaderboard */}
          <Box display={{ base: 'block', lg: 'none' }} gridColumn="1">
            <VStack spacing={4}>
              <Button
                leftIcon={<Search size={18} />}
                bg="linear-gradient(135deg, #00d9d9 0%, #0088aa 100%)"
                color="white"
                fontWeight="bold"
                _hover={{ opacity: 0.9 }}
                onClick={handleSearchOpen}
                w="100%"
                h="50px"
                borderRadius="xl"
              >
                Find Players
              </Button>

              {/*  Activity Feed - Mobile (compact) */}

              <ActivityFeed maxItems={5} compact={true} showHeader={true} showViewAll={true} />

              <Box data-tutorial="leaderboard">
                <LeaderboardPanel currentUid={currentUid} maxEntries={5} />
              </Box>

              {/*  Monthly Challenge - Mobile */}

              <MonthlyChallengeCard currentUid={currentUid} maxEntries={3} compact />
            </VStack>
          </Box>
        </Grid>

        {/* Daily Bonus Modal */}
        {/* One-time "Host a home game" nudge; waits until no tour or other modal is up */}
        <HomeGamePromoModal uid={currentUid} />

        <Modal isOpen={isOpen} onClose={onClose} isCentered>
          <ModalOverlay bg="blackAlpha.900" />
          <ModalContent
            bg="linear-gradient(135deg, #1a0a0a 0%, #2d1810 100%)"
            border="3px solid"
            borderColor="yellow.500"
            borderRadius="2xl"
            mx={4}
            boxShadow="0 0 60px rgba(255,215,0,0.4)"
          >
            <ModalHeader textAlign="center" color="yellow.400" fontSize="2xl">
               Daily Bonus!

            </ModalHeader>
            <ModalCloseButton color="yellow.400" />
            <ModalBody pb={8}>
              <VStack spacing={6}>
                <Box
                  bg="linear-gradient(135deg, rgba(255,215,0,0.3) 0%, rgba(255,215,0,0.1) 100%)"
                  p={6}
                  borderRadius="full"
                  border="3px solid"
                  borderColor="yellow.500"
                  animation={`${float} 2s ease-in-out infinite`}
                >
                  <Gift color="#fbbf24" size={64} />
                </Box>
                
                <VStack spacing={2}>
                  <Text color="white" fontSize="xl" fontWeight="bold">
                    You received
                  </Text>
                  <Text color="yellow.400" fontSize="5xl" fontWeight="black">
                    +{bonusAmount.toLocaleString()}
                  </Text>
                  <Text color="yellow.300" fontSize="xl" fontWeight="bold">
                    PlayCoins!
                  </Text>
                </VStack>
                
                <Text color="gray.400" textAlign="center" fontSize="sm">
                  Come back tomorrow for another bonus!
                </Text>
                
                <PokerButton 
                  variant="primary" 
                  onClick={onClose} 
                  w="full"
                  bg="linear-gradient(135deg, #dc2626 0%, #991b1b 100%)"
                  border="2px solid"
                  borderColor="yellow.500"
                  _hover={{ transform: 'scale(1.02)' }}
                >
                  Awesome! 

                </PokerButton>
              </VStack>
            </ModalBody>
          </ModalContent>
        </Modal>

        {/* Admin Credit Modal */}
        <Modal isOpen={isAdminCreditOpen} onClose={onAdminCreditClose} isCentered>
          <ModalOverlay bg="blackAlpha.900" />
          <ModalContent
            bg="linear-gradient(135deg, #0a1a0a 0%, #102d10 100%)"
            border="3px solid"
            borderColor="green.500"
            borderRadius="2xl"
            mx={4}
            boxShadow="0 0 60px rgba(34,197,94,0.4)"
          >
            <ModalHeader textAlign="center" color="green.400" fontSize="2xl">
               Special Bonus!

            </ModalHeader>
            <ModalCloseButton color="green.400" />
            <ModalBody pb={8}>
              <VStack spacing={6}>
                <Box
                  bg="linear-gradient(135deg, rgba(34,197,94,0.3) 0%, rgba(34,197,94,0.1) 100%)"
                  p={6}
                  borderRadius="full"
                  border="3px solid"
                  borderColor="green.500"
                  animation={`${float} 2s ease-in-out infinite`}
                >
                  <Star color="#22c55e" size={64} />
                </Box>
                
                <VStack spacing={2}>
                  <Text color="white" fontSize="lg" textAlign="center" px={4}>
                    {adminCredit?.message}
                  </Text>
                  <Text color="green.400" fontSize="5xl" fontWeight="black">
                    +{adminCredit?.amount.toLocaleString()}
                  </Text>
                  <Text color={adminCredit?.currency === 'pkcash' ? 'green.300' : 'yellow.300'} fontSize="xl" fontWeight="bold">
                    {adminCredit?.currency === 'pkcash' ? 'PSS Coins' : 'PlayCoins'}!
                  </Text>
                </VStack>
                
                {adminCredit?.currency === 'pkcash' && (
                  <Text color="gray.400" textAlign="center" fontSize="sm">
                    Note: PSS Coins require playthrough before redemption
                  </Text>
                )}
                
                <PokerButton 
                  variant="primary" 
                  onClick={onAdminCreditClose} 
                  w="full"
                  bg="linear-gradient(135deg, #22c55e 0%, #15803d 100%)"
                  border="2px solid"
                  borderColor="green.300"
                  _hover={{ transform: 'scale(1.02)' }}
                >
                  Claim Bonus! 

                </PokerButton>
              </VStack>
            </ModalBody>
          </ModalContent>
        </Modal>

        {/* User Search Modal -  UPDATED with defaultTab prop */}

        <UserSearch
          isOpen={isSearchOpen}
          onClose={handleSearchClose}
          currentUid={currentUid || ''}
          currentUserName={currentUsername}
          defaultTab={searchDefaultTab}
        />

        {/* 🎡 Daily wheel modal. The wallet credit happens server-side and
            the header balance is a live subscription, so it updates itself -
            this just echoes the win the way the daily bonus does. */}
        {currentUid && (
          <PackDrop
            isOpen={packOpen}
            onClose={() => setPackOpen(false)}
            onWin={(amount, kind) => {
              toast({
                title: 'Pack opened',
                description: kind === 'pkcash'
                  ? `$${amount} PSS Coins added — 1x playthrough, then it's redeemable.`
                  : `${amount.toLocaleString()} PlayCoins added to your balance.`,
                status: 'success', duration: 5000, isClosable: true, position: 'top',
              });
            }}
          />
        )}

        {currentUid && (
          <WheelSpin
            userId={currentUid}
            isOpen={wheelOpen}
            onClose={() => setWheelOpen(false)}
            onWin={(amount, type) => {
              toast({
                title: type === 'jackpot' ? 'Top prize!'
                  : type === 'pkcash' ? 'PSS Coins won'
                  : 'Daily Spin',
                description: amount > 0
                  // PSS Coins are the redeemable currency - say so, and never call them PlayCoins.
                  ? (type === 'pkcash'
                      ? `You won $${Number(amount).toFixed(2)} in PSS Coins. Play them through once to redeem.`
                      : `You won ${amount.toLocaleString()} PlayCoins!`)
                  : 'Better luck tomorrow!',
                status: 'success',
                duration: 5000,
                isClosable: true,
                position: 'top',
              });
            }}
          />
        )}
      </Container>

      {/*  FLOATING ACTIVE PLAYERS WIDGET */}

      <FloatingActivePlayersWidget 
        count={activeUsersCount} 
        onClick={handleActivePlayersClick} 
      />
    </Box>
  );
};

export default HomePage;